import { deployEnv } from "../constants";

const SESSION_KEY = "mass_ticketing_session_id";

/**
 * Canonical event names. The Klaviyo metric uses the string as-is; the GTM
 * dataLayer receives a snake_case version of the same name.
 */
export const EVENTS = Object.freeze({
  // Anonymous — read these from GTM, not Klaviyo (see spec section 5.1).
  VIEWED_BOOKING_PAGE: "Viewed Booking Page",
  ADDED_TICKET: "Added Ticket",
  REMOVED_TICKET: "Removed Ticket",
  CLICKED_CHECKOUT: "Clicked Checkout",
  LOGIN_WALL_SHOWN: "Login Wall Shown",
  // Identified.
  LOGGED_IN: "Logged In",
  STARTED_CHECKOUT: "Started Checkout",
  VIEWED_BOOKING_STEP: "Viewed Booking Step",
  SUBMITTED_GUESTS_INFO: "Submitted Guests Info",
  ACCEPTED_TERMS: "Accepted Terms",
  INITIATED_PAYMENT: "Initiated Payment",
  // Failure / exit.
  ADD_TO_CART_FAILED: "Add To Cart Failed",
  CART_EXPIRED: "Cart Expired",
});

const toSnakeCase = (name) => name.toLowerCase().replace(/\s+/g, "_");

/**
 * Fabricated guest emails are minted as `${timestamp}-${index}-gguest-${realEmail}`
 * by GuestsInfo.jsx and by the backend mass-ticketing serializer. Identifying
 * them would create junk Klaviyo profiles and wreck list health.
 */
export const isGeneratedGuestEmail = (email) =>
  typeof email === "string" && email.includes("-gguest-");

const makeFallbackId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Prefers a real RFC 4122 UUID via crypto.randomUUID() (per the module's
 * documented `getSessionId() -> string — UUID` contract). Falls back to a
 * compact non-UUID id when the API is unavailable (older browsers,
 * non-secure contexts) or throws.
 */
const makeId = () => {
  try {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
  } catch (e) {
    // fall through to the non-UUID fallback below
  }
  return makeFallbackId();
};

let cachedSessionId = null;

/**
 * A per-tab id used to stitch a visitor's anonymous pre-login activity to the
 * person they turn out to be once they log in. Falls back to an in-memory value
 * when sessionStorage is unavailable (private browsing).
 */
export const getSessionId = () => {
  if (cachedSessionId) return cachedSessionId;
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      cachedSessionId = stored;
      return cachedSessionId;
    }
    cachedSessionId = makeId();
    sessionStorage.setItem(SESSION_KEY, cachedSessionId);
  } catch (e) {
    cachedSessionId = cachedSessionId || makeId();
  }
  return cachedSessionId;
};

let currentUserId = null;

const baseProps = () => {
  const props = { sessionId: getSessionId(), deployEnv };
  if (currentUserId != null) props.userId = currentUserId;
  return props;
};

/**
 * Fire-and-forget. Never throws, never returns a value, never blocks.
 * A failure here must never affect the booking flow.
 */
export const track = (eventName, props = {}) => {
  try {
    const payload = { ...baseProps(), ...props };
    try {
      if (window.klaviyo && typeof window.klaviyo.track === "function") {
        window.klaviyo.track(eventName, payload);
      }
    } catch (e) {
      // Klaviyo failing must not stop the dataLayer push.
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("[tracking] klaviyo.track failed", eventName, e);
      }
    }
    try {
      if (window.dataLayer && typeof window.dataLayer.push === "function") {
        window.dataLayer.push({ event: toSnakeCase(eventName), ...payload });
      }
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("[tracking] dataLayer.push failed", eventName, e);
      }
    }
  } catch (e) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[tracking] failed", eventName, e);
    }
  }
  return undefined;
};

/**
 * Attaches subsequent events to a real person. Email only — see spec section 5.2
 * for why external_id is deliberately omitted.
 */
export const identifyUser = (user) => {
  try {
    const email = user && user.email;
    if (!email || isGeneratedGuestEmail(email)) return undefined;
    currentUserId = user.pk != null ? user.pk : null;
    const profile = { $email: email };
    if (user.firstName) profile.$first_name = user.firstName;
    if (user.lastName) profile.$last_name = user.lastName;
    if (window.klaviyo && typeof window.klaviyo.identify === "function") {
      window.klaviyo.identify(profile);
    }
  } catch (e) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[tracking] identifyUser failed", e);
    }
  }
  return undefined;
};

/**
 * Clears identity so a shared device does not attribute to the previous
 * user. Also clears Klaviyo's own client-side identity via its reset()
 * method, if present — reset() is called defensively (existence-checked,
 * try/catch-guarded) because it may not exist in all klaviyo.js versions.
 */
export const resetIdentity = () => {
  try {
    currentUserId = null;
  } catch (e) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[tracking] resetIdentity failed to clear currentUserId", e);
    }
  }
  try {
    if (window.klaviyo && typeof window.klaviyo.reset === "function") {
      window.klaviyo.reset();
    }
  } catch (e) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[tracking] klaviyo.reset failed", e);
    }
  }
  return undefined;
};
